import { useCallback, useMemo } from 'react';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { usePostHog } from 'posthog-js/react';

// R2 AC 6 source allowlist — anything outside this list is normalised to
// `source='unknown'` but the raw slug is preserved in `rawSource` so PostHog
// can surface mistyped deeplinks via the billing_modal_unknown_source event.
const SOURCE_ALLOWLIST = new Set([
  'pastdue_d1',
  'pastdue_d3',
  'pastdue_d5',
  'pastdue_d9',
  'pause_d7',
  'pause_d14',
  'pause_d21',
  'pause_d28',
  // J06 Pause Confirmation (screen-17) and J07 Pause Pre-lapse (screen-18)
  // Dittofeed CTA slugs. Required ridealong to phase 1.5 §1.30.a per
  // phase_1_5_audit_fixes.md (Dittofeed templates updated 2026-04-30, but
  // the code-side allowlist was missed until sonnet review caught it).
  'pause_confirm',
  'pause_prelapse',
  'settings',
  'profile',
  'paddle_portal_return',
  // In-app deeplinks fired by HeaderCTA / MembershipSection for paused /
  // cancelled-in-grace states so BillingModal opens in resume / reactivate
  // mode instead of triggerUpgradeModal (which would start a fresh Paddle
  // checkout and leave the existing subscription unresolved).
  'paused_header_cta',
  'paused_grace_cta',
  'cancelled_grace_cta',
  'membership_resume',
  'membership_reactivate',
  // /pricing tile CTAs for paying/grace users: Switch to X, Resume X,
  // Update payment. BillingModal opens with the matching `action=` param.
  'pricing_page',
]);

// R2 AC 7 action allowlist.
const ACTION_ALLOWLIST = new Set([
  'update_card',
  'cancel',
  'switch_plan',
  'reactivate',
  'resume',
  'refresh',
]);

export function useBillingDeeplink() {
  const params = useLocalSearchParams();
  const router = useRouter();
  const posthog = usePostHog();

  const rawBilling = String(params?.billing ?? '');
  const rawSource = String(params?.source ?? '');
  const rawAction = String(params?.action ?? '');

  const source = SOURCE_ALLOWLIST.has(rawSource) ? rawSource : (rawSource ? 'unknown' : null);
  const action = ACTION_ALLOWLIST.has(rawAction) ? rawAction : null;

  const shouldAutoOpen = rawBilling === 'open';

  // Read feature flag synchronously from PostHog's cached value. If the flag
  // is off, the modal stays closed regardless of the deeplink (kill-switch).
  const flagEnabled = useMemo(() => {
    try {
      const val = posthog?.isFeatureEnabled?.('billing-modal-enabled');
      // Treat undefined as enabled — fallback for unauthenticated first mounts.
      return val !== false;
    } catch {
      return true;
    }
  }, [posthog]);

  const clearDeeplink = useCallback(() => {
    // Strip the modal params from the URL without causing a full reload.
    // MembershipSection (the deeplink consumer) lives on /profile, so we
    // stay on /profile — replacing to /settings here would dismount the
    // very component that owns the deeplink.
    router.replace('/profile');
  }, [router]);

  return {
    shouldAutoOpen: shouldAutoOpen && flagEnabled,
    flagEnabled,
    source,
    rawSource: rawSource || null,
    action,
    rawAction: rawAction || null,
    clearDeeplink,
  };
}
